// =============================================================================
// hooks/sales/useLeadSearch.ts — Busca de leads para o seletor de "Novo
// agendamento" da Agenda comercial (F35-S24, doc 26 §6.1).
//
// GET /api/leads?search=&limit= é a MESMA rota do CRM (`leads:read`) — o
// service (`apps/api/.../leads/service.ts` → `listLeads` → `findLeads`)
// já recebe `actor.organizationId` + `actor.cityScopeIds` e filtra no banco.
// Um lead fora do escopo do ator NUNCA chega ao navegador: não há filtro
// client-side aqui porque não há nada para filtrar — o vazamento que a nota
// do slot pede para evitar (dado chegar ao cliente) já é impedido no
// servidor. Ver LeadListQuerySchema/LeadListResponseSchema em
// packages/shared-schemas/src/leads.ts (conferido antes de assumir formato).
//
// 403 (sem `leads:read`) vira estado PRÓPRIO (`isForbidden`), nunca
// "nenhum resultado" — o vendedor sem a permissão de busca precisa saber
// que o problema é acesso, não que a base está vazia (mesmo padrão de
// hooks/dashboard/useDashboardMetrics.ts e features/relatorios/hooks/*).
// =============================================================================

import type { LeadResponse } from '@elemento/shared-schemas';
import { LeadListResponseSchema } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { ApiError, api } from '../../lib/api';

export type { LeadResponse };

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 10;

/**
 * Monta a querystring de `GET /api/leads`. Pura e exportada para teste: só
 * `search` e `limit` — nenhum parâmetro de cidade/agente/organização é
 * adicionado aqui. O escopo é decidido inteiramente pelo servidor a partir
 * do ator autenticado (`actor.cityScopeIds`), nunca pelo cliente — não há
 * "filtro" para vazar porque este componente não sabe (e não pode saber)
 * quais cidades existem fora do escopo do usuário.
 */
export function buildLeadSearchQuery(term: string): string {
  return new URLSearchParams({ search: term, limit: String(RESULT_LIMIT) }).toString();
}

async function fetchLeadSearch(term: string): Promise<LeadResponse[]> {
  const raw = await api.get<unknown>(`/api/leads?${buildLeadSearchQuery(term)}`);
  // Validação runtime: drift de contrato front x API não é pego pelo CI
  // (nota do slot) — parse defensivo contra o schema REAL do backend.
  return LeadListResponseSchema.parse(raw).data;
}

export const leadSearchKey = (term: string) => ['sales', 'agenda', 'lead-search', term] as const;

export interface UseLeadSearchResult {
  results: LeadResponse[];
  isFetching: boolean;
  /** Query ainda não tem termo suficiente (< 2 chars) — não é erro nem vazio. */
  isIdle: boolean;
  /** 403 do servidor: ator sem `leads:read`. Nunca tratado como "sem resultado". */
  isForbidden: boolean;
  /** Erro de rede/servidor que não é 403. */
  isError: boolean;
  refetch: () => void;
}

export function useLeadSearch(query: string): UseLeadSearchResult {
  const [debounced, setDebounced] = React.useState(query);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const trimmed = debounced.trim();
  const enabled = trimmed.length >= MIN_QUERY_LENGTH;

  const {
    data,
    isFetching,
    isError: queryIsError,
    error,
    refetch,
  } = useQuery({
    queryKey: leadSearchKey(trimmed),
    queryFn: () => fetchLeadSearch(trimmed),
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const isForbidden = queryIsError && error instanceof ApiError && error.status === 403;

  return {
    results: data ?? [],
    isFetching,
    isIdle: !enabled,
    isForbidden,
    isError: queryIsError && !isForbidden,
    refetch: () => void refetch(),
  };
}
